// noinspection SpellCheckingInspection

import React, { createContext, useEffect, useState } from "react";
import useAuth from "../hooks/useAuth";
import axios from "../utils/bxApi";

type TConfig = string | number | boolean | null | undefined;

interface ClientConfig {
    [key: string]: TConfig
}

interface WebManagerConfig extends ClientConfig {
    showlabels: boolean,
    showstockcount: boolean,
    showstockcountimport: boolean,
    showpickingmonitor: boolean,
    showreceivingmonitor: boolean,
    showmailarchive: boolean,
    showshipment: boolean,
    showstockoverview: boolean,
    showlocation: boolean,
    showlabeldesigner: boolean,
    showwebshop: boolean,
    showassetsmanager: boolean,
    showproductsmanager: boolean,
    autolabelscolumns: boolean,
    genericreports: undefined | null | string,
    plugin_norgescatering: boolean,
    plugin_raumaullvare: boolean,
    plugin_buer: boolean
}

interface WebShopConfig extends ClientConfig {
    priceformat?: string,
    changeaddress: boolean,
    showdeliverymethod: boolean,
    showknownaddresses: boolean,
    showlistprice: boolean,
    showsupplierproductno: boolean,
    showsuppliername: boolean,
    showrestock: boolean,
    showmessage: boolean,
    shownetprice: boolean,
    showdeldate: boolean,
    showcustpo: boolean,
    showyourref: boolean,
    showorderlabel: boolean,
    showdelcontactname: boolean,
    showdelmobile: boolean,
    ordercommentproductno: boolean,
    copyfromhistory: boolean,
}

interface StockCount extends ClientConfig {
    uselists: boolean
}

interface AssetsConfig extends ClientConfig {
    label?: string,
    printer?: string,
    printcopies: number
}

interface ClientConfigs {
    isLoading: ClientConfig,
    webmanager: WebManagerConfig,
    stockcount: StockCount,
    webshop: WebShopConfig,
    manualimports: ClientConfig,
    assets: AssetsConfig,

    [key: string]: ClientConfig
}

export const initClientConfig: ClientConfigs = {
    isLoading: {
        isLoading: true
    },
    webmanager: {
        showlabels: true,
        showstockcount: false,
        showstockcountimport: false,
        showreceivingmonitor: false,
        showpickingmonitor: false,
        showmailarchive: false,
        showshipment: false,
        showstockoverview: false,
        showlocation: false,

        showlabeldesigner: true,

        showwebshop: false,
        showassetsmanager: false,

        showproductsmanager: false,

        autolabelscolumns: false,

        widgets: false,

        genericreports: undefined,

        plugin_norgescatering: false,
        plugin_raumaullvare: false,
        plugin_buer: false
    },
    stockcount: {
        uselists: false
    },
    webshop: {
        changeaddress: true,
        showsupplierproductno: true,
        showsuppliername: false,
        showrestock: false,
        showmessage: false,
        showlistprice: true,
        shownetprice: true,
        showdeliverymethod: true,
        showdeldate: true,
        showknownaddresses: false,
        showcustpo: true,
        showorderlabel: true,
        showdelcontactname: false,
        showdelmobile: true,
        ordercommentproductno: false,
        showyourref: true,
        copyfromhistory: true
    },
    manualimports: {
        products: false
    },
    assets: {
        printcopies: 1,
        showproject: true,
        showassetid: true,
        showbrand: true,
        showmodel: true,
        showowner: true,
        showmatchno: true,
        showname: true,
        showcreated: true
    }
}

export const ClientConfigContext = createContext<ClientConfigs | null>(null);

const parseAndMergeClientConfig = (records: any[]) => {
    if (records.length === 0) {
        //Ungen endring om records er tomt, returnerer samme object for å ikke trigge ny state
        return initClientConfig;
    }
    let settings: ClientConfigs = { ...initClientConfig };
    records.forEach((row: any) => {
        for (const area in row) {
            try {
                if (!settings[area]) {
                    settings[area] = {}
                }
                row[area].forEach((configItem: any) => {
                    const { name, value } = configItem;
                    settings[area] = {
                        ...settings[area],
                        [name]: value === 'false' ? false : value
                    }
                })

            } catch (err) {
                //Catcher feil i BxEngine ved overstyring av license-config, der clientConfig som skal overstyres mangler
                console.error(err);
            }
        }
    })
    return settings;
}

const ClientConfigManager = (): ClientConfigs => {
    const { companyCode, isAuthenticated } = useAuth();
    const [clientConfig, setClientConfig] = useState<ClientConfigs>(initClientConfig);

    useEffect(() => {
        const fetchClientConfigFromServer = async () => {
            try {
                const { data } = await axios.get(`/${companyCode}/1.0/config`);
                setClientConfig({
                    ...parseAndMergeClientConfig(data.data),
                    isLoading: {
                        value: false
                    }
                });
            } catch (err) {
                console.error(err);
            }
        }

        if (isAuthenticated && companyCode) {
            // noinspection JSIgnoredPromiseFromCall
            fetchClientConfigFromServer();
        }

    }, [companyCode, isAuthenticated, setClientConfig])
    return { ...clientConfig }
}

export const ClientConfigProvider = ({ children }: { children: React.ReactNode }) =>
    (
        <ClientConfigContext.Provider value={ClientConfigManager()}>{children}</ClientConfigContext.Provider>
    )

